import { getPowerLoreTheme } from '@/lib/power-lore';
import type { ConductAppearance } from '@/lib/conduct-presentation';

const lore = getPowerLoreTheme('normal');

/** Azul do rodapé (`to-[#000b6a]`) — acentos do light mode. */
const FOOTER_BLUE = '#000b6a';
const FOOTER_BLUE_RGB = '0, 11, 106';

/**
 * Tokens da página /regras — mesmas cores dos cards da Conduta (tema lore Normal).
 */
export const regrasTokens = {
  pageBg: 'bg-[#030023]',
  /** Mesmo fundo dos cards da Conduta. */
  cardBg: 'bg-[#030023]/80',
  cardBgHover: 'hover:bg-sky-400/[0.06]',
  border: 'border-sky-500/20',
  borderHover: 'hover:border-sky-400/40',
  borderActive: 'border-sky-400/45',
  textPrimary: 'text-white',
  textSecondary: 'text-[#AEB7D6]',
  textMuted: 'text-sky-400/60',
  accent: 'text-sky-300',
  accentSoft: 'bg-sky-400/10',
  /** Número de regra — glow das bolinhas da Lore. */
  indexGlow:
    'text-sky-400/90 [text-shadow:0_0_12px_rgba(56,189,248,0.55),0_0_22px_rgba(56,189,248,0.35)]',
  sideBar: 'bg-sky-400/50 group-hover:bg-sky-400/80',
  radius: 'rounded-2xl',
  gap: 'gap-3',
  cardShadow: lore.cardShadow,
  filterTray:
    'border-sky-500/20 bg-[#030023]/85 shadow-[0_16px_48px_-28px_rgba(0,0,0,0.85)] ring-1 ring-sky-400/15',
  filterInactive:
    'border-transparent bg-transparent text-blue-100/70 hover:bg-sky-400/10 hover:text-sky-100',
  filterActive:
    'border-sky-400/25 bg-sky-500/15 text-sky-50 shadow-[inset_0_1px_0_rgba(125,211,252,0.12)]',
  filterIconInactive: 'border-sky-400/20 bg-sky-400/10 text-sky-300/75',
  filterIconActive: 'border-sky-400/30 bg-sky-400/15 text-sky-200',
  filterCountInactive: 'bg-sky-400/10 text-sky-400/65',
  filterCountActive: 'bg-sky-400/20 text-sky-100',
  iconShell:
    'border-sky-500/20 bg-sky-400/10 text-sky-200 group-hover:border-[rgba(80,110,200,0.38)] group-hover:text-sky-100',
  chevronShell:
    'border-sky-500/20 bg-sky-400/10 text-sky-200 group-hover:border-[rgba(80,110,200,0.38)] group-hover:text-sky-50',
  badge: 'border-sky-500/20 bg-sky-400/10 text-sky-100',
  badgeUpdated: 'border-sky-500/20 bg-sky-400/10 text-sky-200',
  expandWash: 'from-sky-400/[0.06]',
  cta:
    'border-sky-400/40 bg-sky-500/20 text-sky-50 shadow-[0_0_28px_-10px_rgba(56,189,248,0.55)] hover:border-sky-400/55 hover:bg-sky-500/30',
} as const;

export const regrasTokensLight = {
  pageBg: 'bg-[#F4F7FC]',
  cardBg: 'bg-white',
  cardBgHover: 'hover:bg-[rgba(0,11,106,0.03)]',
  border: 'border-[rgba(0,11,106,0.14)]',
  borderHover: 'hover:border-[rgba(0,11,106,0.28)]',
  borderActive: 'border-[rgba(0,11,106,0.32)]',
  textPrimary: 'text-slate-900',
  textSecondary: 'text-slate-600',
  textMuted: 'text-slate-400',
  accent: 'text-[#000b6a]',
  accentSoft: 'bg-[rgba(0,11,106,0.07)]',
  indexGlow:
    'text-[rgba(0,11,106,0.82)] [text-shadow:0_0_10px_rgba(0,11,106,0.22),0_0_18px_rgba(0,11,106,0.12)]',
  sideBar: 'bg-[rgba(0,11,106,0.4)] group-hover:bg-[rgba(0,11,106,0.7)]',
  radius: 'rounded-2xl',
  gap: 'gap-3',
  cardShadow: 'shadow-[0_12px_40px_-24px_rgba(0,11,106,0.2)]',
  filterTray:
    'border-[rgba(0,11,106,0.1)] bg-white/95 shadow-[0_16px_48px_-28px_rgba(0,11,106,0.28)] ring-1 ring-[rgba(0,11,106,0.06)]',
  filterInactive:
    'border-transparent bg-transparent text-slate-500 hover:bg-[rgba(0,11,106,0.04)] hover:text-[#000b6a]',
  filterActive:
    'border-[rgba(0,11,106,0.25)] bg-[#000b6a] text-white shadow-[0_8px_24px_-12px_rgba(0,11,106,0.45)]',
  filterIconInactive: 'border-[rgba(0,11,106,0.14)] bg-[rgba(0,11,106,0.05)] text-[rgba(0,11,106,0.75)]',
  filterIconActive: 'border-white/25 bg-white/15 text-white',
  filterCountInactive: 'bg-[rgba(0,11,106,0.07)] text-slate-500',
  filterCountActive: 'bg-white/15 text-white/90',
  iconShell:
    'border-[rgba(0,11,106,0.14)] bg-[rgba(0,11,106,0.05)] text-[rgba(0,11,106,0.8)] group-hover:border-[rgba(0,11,106,0.28)] group-hover:bg-[rgba(0,11,106,0.08)] group-hover:text-[#000b6a]',
  chevronShell:
    'border-[rgba(0,11,106,0.14)] bg-[rgba(0,11,106,0.05)] text-[rgba(0,11,106,0.8)] group-hover:border-[rgba(0,11,106,0.28)] group-hover:bg-[rgba(0,11,106,0.08)] group-hover:text-[#000b6a]',
  badge: 'border-[rgba(0,11,106,0.14)] bg-[rgba(0,11,106,0.06)] text-[#000b6a]',
  badgeUpdated: 'border-[rgba(0,11,106,0.16)] bg-[rgba(0,11,106,0.07)] text-[rgba(0,11,106,0.88)]',
  expandWash: 'from-[rgba(0,11,106,0.04)]',
  cta:
    'border-[rgba(0,11,106,0.22)] bg-[rgba(0,11,106,0.08)] text-[#000b6a] shadow-[0_0_24px_-12px_rgba(0,11,106,0.3)] hover:border-[rgba(0,11,106,0.35)] hover:bg-[rgba(0,11,106,0.12)]',
} as const;

export type RegrasAppearanceTokens = typeof regrasTokens | typeof regrasTokensLight;

export function getRegrasTokens(appearance: ConductAppearance = 'dark'): RegrasAppearanceTokens {
  return appearance === 'light' ? regrasTokensLight : regrasTokens;
}

export function getRegrasSurfaceClass(t: RegrasAppearanceTokens) {
  return [t.cardBg, t.border, t.radius, t.cardShadow, 'border backdrop-blur-sm'].join(' ');
}

export function getRegrasCardInteractiveClass(
  t: RegrasAppearanceTokens,
  appearance: ConductAppearance = 'dark'
) {
  return [
    getRegrasSurfaceClass(t),
    t.cardBgHover,
    t.borderHover,
    'transition-[border-color,background-color,box-shadow,transform] duration-200',
    appearance === 'light'
      ? 'hover:-translate-y-px hover:shadow-[0_12px_36px_-20px_rgba(0,11,106,0.25)]'
      : 'hover:-translate-y-px hover:shadow-[0_0_32px_-16px_rgba(56,189,248,0.45)]',
  ].join(' ');
}

export const regrasSurfaceClass = getRegrasSurfaceClass(regrasTokens);

export const regrasCardInteractiveClass = getRegrasCardInteractiveClass(regrasTokens, 'dark');

export { FOOTER_BLUE as REGRAS_FOOTER_BLUE, FOOTER_BLUE_RGB as REGRAS_FOOTER_BLUE_RGB };
